import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { JobsAPI } from '../../api/client';
import InferenceJobForm from '../../components/Forms/Inference/InferenceJobForm';
import PageWrapper from '../../components/Layout/PageWrapper';
import SkeletonJobForm from '../../components/Common/SkeletonJobForm';
import { useJobPolling } from '../../hooks/useJobPolling';
import {
  buildQuery,
  parseQueryToFormData
} from '../../components/Forms/FormUtils';

const InferenceJobDetailPage = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Get metadata from store
  const metadataSlice = useSelector(state => state.metadata);

  const fetchJobData = useCallback(async () => {
    try {
      const data = await JobsAPI.retrieve(id);
      setJob(data);

      // Only update form data if not already set to avoid overriding user edits
      if (!formData && data.query) {
        const parsed = parseQueryToFormData(data.query);
        parsed.name = data.name || '';
        parsed.description = data.description || '';
        setFormData(parsed);
      }
    } catch (e) {
      if (!job) setError('Failed to load job');
    }
  }, [id, formData, job]);

  // Initial load
  useEffect(() => {
    const loadInitialJob = async () => {
      try {
        const data = await JobsAPI.retrieve(id);
        setJob(data);

        // Parse the query into form data structure
        if (data.query) {
          const parsed = parseQueryToFormData(data.query);
          // Add job-specific fields
          parsed.name = data.name || '';
          parsed.description = data.description || '';
          setFormData(parsed);
        }
      } catch (e) {
        setError('Failed to load job');
      } finally {
        setLoading(false);
      }
    };

    if (id) loadInitialJob();
  }, [id]);

  // Set up polling
  useJobPolling(job, fetchJobData);

  const handleSave = async () => {
    setSaving(true);
    try {
      const query = buildQuery(formData);
      await JobsAPI.update(id, { query });
      setIsEditing(false);
      // Reload job data
      const updatedJob = await JobsAPI.retrieve(id);
      setJob(updatedJob);
    } catch (e) {
      setError('Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonJobForm isNewJob={false} showEditButtons={isEditing} />;
  if (error) return (
    <PageWrapper>
      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-medium text-red-800">Error loading experiment</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
  if (!job || !formData) return null;

  const editControls = (
    <>
      {!isEditing && (
        <button
          onClick={() => setIsEditing(true)}
          className="bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700 transition-colors"
        >
          Edit
        </button>
      )}
      {isEditing && (
        <>
          <button
            onClick={() => setIsEditing(false)}
            className="bg-gray-500 text-white rounded px-4 py-2 hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 text-white rounded px-4 py-2 hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    </>
  );

  return (
    <InferenceJobForm
      formData={formData}
      setFormData={setFormData}
      isEditing={isEditing}
      metadataFields={metadataSlice.fields.data || []}
      distinctValues={metadataSlice.distinct.data || {}}
      job={job}
      title={`Experiment > ${job.id}`}
      showEditButtons={true}
      editControls={editControls}
    />
  );
};

export default InferenceJobDetailPage;
